<template>
  <header class="topbar">
    <div class="topbar-content">
      <!-- Left: Logo and Hamburger menu -->
      <div class="topbar-left">
        <Button
          icon="pi pi-bars"
          class="p-button-text p-button-lg hamburger-btn"
          @click="toggleSidebar"
          aria-label="Toggle menu"
          data-testid="topbar-toggle-sidebar"
        />

        <div class="logo-container" v-if="logoUrl">
          <img
            :src="logoUrl"
            :alt="logoAlt"
            class="tenant-logo"
            @error="handleLogoError"
          />
        </div>
      </div>

      <!-- Center: Filter integration -->
      <div class="topbar-center">
        <!-- Container for FiltroHeaderLinks integration -->
        <div id="filtroContainer" class="filtro-container"></div>
        
        <!-- FiltroPage component for functionality -->
        <FiltroPage
          ref="filtroPageRef"
          componentId="topbar-filtro"
          headerContainerId="filtroContainer"
          @filter-applied="handleFilterApplied"
        />
      </div>

      <!-- Right: User info -->
      <div class="topbar-right">
        <!-- Notificação fica na barra porque é o único lugar visível em TODAS
             as telas. No botão flutuante de suporte ela só aparecia para quem
             já tinha decidido pedir ajuda — o contrário de notificar. -->
        <ChamadosSino />
        <TutorialReplayButton />
        <div class="user-info" v-if="user">
          <div class="user-avatar">
            <Avatar
              v-if="user.picture"
              :key="user.picture"
              :image="user.picture"
              shape="circle"
              size="normal"
            />
            <Avatar
              v-else
              :label="userInitials"
              shape="circle"
              size="normal"
            />
          </div>
          <div class="user-details">
            <span class="user-name">{{ user.name }}</span>
            <span class="user-email">{{ user.email }}</span>
          </div>
          
          <!-- User menu dropdown -->
          <Menu
            ref="userMenuRef"
            :model="userMenuItems"
            :popup="true"
          />
          <Button
            icon="pi pi-angle-down"
            class="p-button-text user-menu-btn"
            @click="toggleUserMenu"
            aria-label="User menu"
            data-testid="topbar-user-menu"
          />
        </div>
      </div>
    </div>
  </header>
</template>

<script setup lang="ts">
import { ref, computed, inject, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useToast } from 'primevue/usetoast'
import Button from 'primevue/button'
import Avatar from 'primevue/avatar'
import Menu from 'primevue/menu'
import FiltroPage from '../domains/filtro/FiltroPage.vue'
import ChamadosSino from '../components/ChamadosSino.vue';
import TutorialReplayButton from '../domains/tutorials/TutorialReplayButton.vue'
import { log } from '../utils/DebugLogger'
import { useSidebar } from '../composables/useSidebar'
import { useAuth } from '../composables/useAuth'
import { cacheService } from '../services/cache/index'
import tenantConfig from '../services/tenantConfigService'
import { httpService } from '../services/httpService'
import '../contracts/Permissoes_ResetapermissoesResponse'

// Get router and i18n
const router = useRouter()
const { t } = useI18n()
const toast = useToast()

// Get auth composable for proper logout
const { logout } = useAuth()

// Get permissionsService (injected from main.js)
const permissionsService = inject<any>('permissionsService')

// Get permissions data from service
const getPermissions = (): any => permissionsService?.getPermissionsData()

// Sidebar state
const { toggleSidebar: toggleSidebarState } = useSidebar()

// Template refs
const userMenuRef = ref<any>(null)
const filtroPageRef = ref<any>(null)

// Computed
const user = computed(() => getPermissions()?.user)

const logoUrl = computed((): string => tenantConfig.getLogo())
const logoAlt = computed((): string => tenantConfig.getLogoAlt())

const userInitials = computed((): string => {
  if (!user.value?.name) return '?'

  // Filter out empty strings from split (handles multiple spaces)
  const names: string[] = user.value.name.split(' ').filter((n: string) => n.length > 0)

  if (names.length === 0) return '?'

  if (names.length >= 2) {
    const firstInitial = names[0]?.[0] || ''
    const lastInitial = names[names.length - 1]?.[0] || ''
    return `${firstInitial}${lastInitial}`.toUpperCase()
  }

  return (names[0]?.[0] || '?').toUpperCase()
})

const userMenuItems = computed(() => [
  {
    label: t('Perfil'),
    icon: 'pi pi-user',
    command: () => {
      log('TopBar', 'userMenu', 'Profile clicked')
      router.push('/profile')
    }
  },
  {
    label: t('Configurações'),
    icon: 'pi pi-cog',
    command: () => {
      log('TopBar', 'userMenu', 'Settings clicked')
      // TODO: Navigate to settings page
    }
  },
  {
    separator: true
  },
  {
    label: t('Limpar Cache'),
    icon: 'pi pi-refresh',
    command: () => {
      log('TopBar', 'userMenu', 'Clear cache clicked')
      handleClearCache()
    }
  },
  {
    label: t('Resetar Permissões'),
    icon: 'pi pi-shield',
    command: () => {
      log('TopBar', 'userMenu', 'Reset permissions clicked')
      handleResetPermissions()
    }
  },
  {
    label: t('Sair'),
    icon: 'pi pi-sign-out',
    command: () => {
      log('TopBar', 'userMenu', 'Logout clicked')
      handleLogout()
    }
  }
])

// Methods
const toggleSidebar = (): void => {
  log('TopBar', 'toggleSidebar', 'Toggling sidebar')
  toggleSidebarState()
}

const toggleUserMenu = (event: Event): void => {
  userMenuRef.value.toggle(event)
}

const handleFilterApplied = (filterData: any): void => {
  log('TopBar', 'handleFilterApplied', 'Filter applied from TopBar', filterData)
  // Filter data is now available globally through the composable
}

const handleLogoError = (event: Event): void => {
  log('TopBar', 'handleLogoError', 'Failed to load tenant logo', logoUrl.value);
  // Hide the logo container if image fails to load
  (event.target as HTMLElement).style.display = 'none';
}

const handleLogout = async (): Promise<void> => {
  log('TopBar', 'handleLogout', 'Logging out user')

  // Use proper logout from useAuth composable
  // This ensures tokens and permissions are cleared before redirect
  await logout()
}

const handleResetPermissions = async (): Promise<void> => {
  log('TopBar', 'handleResetPermissions', 'Resetting permissions for current user')
  try {
    await httpService.get('/permissoes/resetaPermissoes', {
      responseContract: 'Permissoes_ResetapermissoesResponse',
    })
    toast.add({
      severity: 'success',
      summary: t('Permissões Resetadas'),
      detail: t('Suas permissões foram recarregadas. A página será atualizada.'),
      life: 2000,
    })
    setTimeout(() => window.location.reload(), 1500)
  } catch (error) {
    log('TopBar', 'handleResetPermissions', 'Error resetting permissions', error)
    toast.add({
      severity: 'error',
      summary: t('Erro'),
      detail: t('Falha ao resetar permissões'),
      life: 3000,
    })
  }
}

const handleClearCache = async (): Promise<void> => {
  log('TopBar', 'handleClearCache', 'Clearing all cache')
  try {
    await cacheService.clear()
    toast.add({
      severity: 'success',
      summary: t('Cache Limpo'),
      detail: t('O cache local foi limpo com sucesso. A página será recarregada.'),
      life: 2000
    })
    // Reload page after short delay to show toast
    setTimeout(() => window.location.reload(), 1500)
  } catch (error) {
    log('TopBar', 'handleClearCache', 'Error clearing cache', error)
    toast.add({
      severity: 'error',
      summary: t('Erro'),
      detail: t('Falha ao limpar o cache'),
      life: 3000
    })
  }
}

// Lifecycle
onMounted(() => {
  log('TopBar', 'mounted', 'TopBar component mounted', {
    logoUrl: logoUrl.value,
    logoAlt: logoAlt.value
  })
})

// Expose methods for external access
defineExpose({
  toggleSidebar,
  getFilterData: () => filtroPageRef.value?.getFilterData()
})
</script>

<style scoped>
.topbar {
  /* Use tenant primary color for background */
  background: var(--app-primary-color);
  border-bottom: 1px solid var(--app-primary-hover-color);
  height: 60px;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.topbar-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 100%;
  padding: 0 20px;
  max-width: 100%;
  gap: 20px;
}

.topbar-left {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 16px;
}

.logo-container {
  display: flex;
  align-items: center;
  height: 40px;
}

.tenant-logo {
  max-height: 40px;
  max-width: 150px;
  width: auto;
  height: auto;
  object-fit: contain;
}

.topbar-center {
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  min-width: 0; /* Allow content to shrink */
}

.topbar-right {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  margin-left: auto;
}

.hamburger-btn {
  /* Use secondary color for icons */
  color: var(--app-secondary-color) !important;
}

.hamburger-btn:hover {
  background-color: rgba(255, 255, 255, 0.1) !important;
}

.filtro-container {
  display: flex;
  align-items: center;
  /* Use secondary color for text */
  color: var(--app-secondary-color);
  font-weight: 500;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  padding: 8px 12px;
  border-radius: 6px;
  transition: background-color 0.2s;
}

.user-info:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.user-details {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
}

.user-name {
  font-weight: 600;
  /* Use secondary color for user name */
  color: var(--app-secondary-color);
  font-size: 14px;
  line-height: 1.2;
}

.user-email {
  /* Slightly lighter secondary color for email */
  color: var(--app-text-color);
  font-size: 12px;
  line-height: 1.2;
}

.user-menu-btn {
  /* Use secondary color for dropdown icon */
  color: var(--app-secondary-color) !important;
  padding: 4px !important;
  min-width: auto !important;
}

.user-menu-btn:hover {
  background-color: rgba(255, 255, 255, 0.1) !important;
}

/* Mobile responsive */
@media (max-width: 768px) {
  .topbar-content {
    padding: 0 12px;
    gap: 12px;
  }

  .topbar-left {
    gap: 12px;
  }

  .logo-container {
    height: 32px;
  }

  .tenant-logo {
    max-height: 32px;
    max-width: 120px;
  }

  .user-details {
    display: none; /* Hide user details on mobile */
  }

  .user-info {
    padding: 6px 8px;
    gap: 8px;
  }

  .filtro-container {
    font-size: 13px;
  }
}

@media (max-width: 480px) {
  .topbar-content {
    padding: 0 8px;
    gap: 8px;
  }

  .topbar-left {
    gap: 8px;
  }

  .logo-container {
    height: 28px;
  }

  .tenant-logo {
    max-height: 28px;
    max-width: 100px;
  }

  .topbar-center {
    flex: 1 1 0; /* Allow shrinking more aggressively */
    overflow: hidden;
  }

  .filtro-container {
    font-size: 12px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
  }

  .hamburger-btn {
    padding: 6px !important;
    min-width: 40px !important;
  }

  .user-menu-btn {
    padding: 2px !important;
  }

  .user-info {
    padding: 4px 6px;
    gap: 6px;
  }
}

/* Tablet */
@media (min-width: 769px) and (max-width: 1024px) {
  .topbar-content {
    padding: 0 16px;
  }
  
  .user-email {
    display: none; /* Hide email on tablet, keep name */
  }
}
</style>
